import React, { useState } from 'react';
import { Mail, Check, Lock, Sparkles } from 'lucide-react';
import { TOKENS, limeBtn, displayFont } from '../../lib/designTokens';
import { trackGenerateLead } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// CRO Audit §3.3 #5 — Calculator tools gate the "save / email me my results" step
// to capture leads at the moment of highest user motivation.
//
// Usage:
//   <EmailResultsGate
//     toolId="longevity-score"
//     toolName="Longevity Score"
//     resultPayload={{ score, breakdown, recommendations }}
//   >
//     {/* The raw result UI — preview shown, full unlocked after email submit */}
//   </EmailResultsGate>
export const EmailResultsGate = ({
  toolId,
  toolName,
  resultPayload,
  children,
  ctaLabel = 'Email me my full results',
}) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: `tool_${toolId}`,
          source: 'tool_result_gate',
          metadata: { tool_id: toolId, tool_name: toolName, result_payload: resultPayload },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setUnlocked(true);
      trackGenerateLead('tool_result_gate', `tool_${toolId}`);
    } catch (err) {
      console.error('Tool result gate submit failed:', err);
      setError('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (unlocked) {
    return (
      <div data-testid={`gate-unlocked-${toolId}`}>
        <div
          className="mb-6 px-4 py-3 flex items-center gap-2 text-xs"
          style={{
            backgroundColor: 'rgba(199,250,62,0.08)',
            border: `1px solid ${TOKENS.limeBorder}`,
            color: TOKENS.lime,
            ...displayFont,
          }}
        >
          <Check className="w-4 h-4" />
          We&apos;ve emailed your full results to {email}.
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="relative" data-testid={`gate-${toolId}`}>
      {/* Preview layer */}
      <div
        className="relative pointer-events-none select-none"
        style={{ maxHeight: '320px', overflow: 'hidden' }}
        aria-hidden="true"
      >
        {children}
      </div>

      {/* Fade-out overlay */}
      <div
        className="absolute inset-x-0 bottom-0 h-48 pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${TOKENS.canvas} 70%)`,
        }}
      />

      {/* Gate form */}
      <div
        className="mt-4 p-6 md:p-8 border"
        style={{
          backgroundColor: TOKENS.surface1,
          borderColor: TOKENS.limeBorder,
        }}
      >
        <div className="flex items-start gap-3 mb-5">
          <Lock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: TOKENS.lime }} />
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.2em] mb-2"
              style={{ color: TOKENS.lime, ...displayFont }}
            >
              <Sparkles className="inline w-3 h-3 mr-1" />
              Unlock your full results
            </p>
            <h3
              className="text-white text-xl md:text-2xl mb-2 leading-tight"
              style={displayFont}
            >
              Save your {toolName} report
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Enter your email to see the complete breakdown, personalized recommendations,
              and a PDF copy you can keep.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid={`gate-form-${toolId}`}>
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full pl-11 pr-4 py-3.5 bg-black/40 border border-zinc-700 text-white text-sm focus:outline-none focus:border-[#C7FA3E] transition-colors"
              disabled={submitting}
              data-testid={`gate-email-${toolId}`}
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm uppercase tracking-wider transition-all duration-300 hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            style={limeBtn}
            data-testid={`gate-submit-${toolId}`}
          >
            {submitting ? 'Sending...' : ctaLabel}
          </button>
        </form>
        {error && <p className="text-red-400 text-xs mt-2" data-testid={`gate-error-${toolId}`}>{error}</p>}
        <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
          We&apos;ll never share your email. Unsubscribe any time. By unlocking you agree to receive
          NinjAthlete&apos;s weekly digest.
        </p>
      </div>
    </div>
  );
};
